import { InputRule, mergeAttributes, Node } from "@tiptap/core"
import { Fragment } from "@tiptap/pm/model"
import { Selection, TextSelection } from "@tiptap/pm/state"

import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import type { EditorState, Transaction } from "@tiptap/pm/state"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		tree: {
			insertTree: () => ReturnType
			toggleTreeFolder: (pos: number) => ReturnType
			indentTreeItem: () => ReturnType
			outdentTreeItem: () => ReturnType
		}
	}
}

// The bare-<details> accordion rule would otherwise claim every folder.
const PARSE_PRIORITY = 1000

interface TreeItem {
	node: ProseMirrorNode
	pos: number
	depth: number
}

// The item the caret is in: a file, or the folder whose name it is in.
function currentItem(state: EditorState): TreeItem | null {
	const { $from } = state.selection
	const parent = $from.parent.type.name
	if (parent === "treeFile") {
		return { node: $from.parent, pos: $from.before(), depth: $from.depth }
	}
	if (parent === "treeFolderName" && $from.depth > 1) {
		const depth = $from.depth - 1
		return { node: $from.node(depth), pos: $from.before(depth), depth }
	}
	return null
}

function placeCaret(tr: Transaction, pos: number): void {
	const clamped = Math.max(0, Math.min(pos, tr.doc.content.size))
	tr.setSelection(TextSelection.near(tr.doc.resolve(clamped)))
}

function indent(state: EditorState, tr: Transaction): boolean {
	const item = currentItem(state)
	if (!item) return false
	const { $from } = state.selection
	const container = $from.node(item.depth - 1)
	const index = $from.index(item.depth - 1)
	// A folder's first child is its name, which never takes items.
	const first = container.type.name === "treeFolder" ? 1 : 0
	if (index <= first) return false

	const previous = container.child(index - 1)
	const previousPos = item.pos - previous.nodeSize
	const { treeFolder, treeFolderName } = state.schema.nodes
	if (!treeFolder || !treeFolderName) return false

	// Nesting under a file makes it a folder, the way Mintlify's list reads.
	const folder =
		previous.type === treeFolder
			? treeFolder.create(
					{ ...previous.attrs, open: true },
					previous.content.append(Fragment.from(item.node)),
				)
			: treeFolder.create({ open: true }, [
					treeFolderName.create(null, previous.content),
					item.node,
				])

	tr.replaceWith(previousPos, item.pos + item.node.nodeSize, folder)
	const movedPos = previousPos + folder.nodeSize - 1 - item.node.nodeSize
	placeCaret(tr, movedPos + ($from.pos - item.pos))
	return true
}

function outdent(state: EditorState, tr: Transaction): boolean {
	const item = currentItem(state)
	if (!item || item.depth < 3) return false
	const { $from } = state.selection
	const folderDepth = item.depth - 1
	const folder = $from.node(folderDepth)
	if (folder.type.name !== "treeFolder") return false
	const folderPos = $from.before(folderDepth)
	const index = $from.index(folderDepth)

	// The item and every sibling after it move out together, so the order holds.
	const kept: ProseMirrorNode[] = []
	const moved: ProseMirrorNode[] = []
	folder.forEach((child, _offset, childIndex) => {
		;(childIndex < index ? kept : moved).push(child)
	})
	const remaining = folder.copy(Fragment.from(kept))
	tr.replaceWith(folderPos, folderPos + folder.nodeSize, [remaining, ...moved])
	placeCaret(tr, folderPos + remaining.nodeSize + ($from.pos - item.pos))
	return true
}

// An empty file takes the caret out of the tree, like an empty list item.
function leaveEmptyFile(state: EditorState, tr: Transaction): boolean {
	const { $from, empty } = state.selection
	if (!empty || $from.parent.type.name !== "treeFile") return false
	if ($from.parent.content.size > 0) return false
	if ($from.depth > 2) return outdent(state, tr)

	const tree = $from.node(1)
	const treePos = $from.before(1)
	const paragraph = state.schema.nodes.paragraph?.create()
	if (!paragraph) return false
	if (tree.childCount === 1) {
		tr.replaceWith(treePos, treePos + tree.nodeSize, paragraph)
		placeCaret(tr, treePos + 1)
		return true
	}
	const filePos = $from.before()
	tr.delete(filePos, filePos + $from.parent.nodeSize)
	const after = tr.mapping.map(treePos + tree.nodeSize)
	tr.insert(after, paragraph)
	placeCaret(tr, after + 1)
	return true
}

function removeEmptyRow(state: EditorState, tr: Transaction): boolean {
	const { $from, empty } = state.selection
	if (!empty || $from.parentOffset > 0) return false
	const type = $from.parent.type.name
	if (type !== "treeFile" && type !== "treeFolderName") return false
	if ($from.parent.content.size > 0) return false

	if (type === "treeFolderName") {
		// An unnamed folder turns back into a file and keeps what it held.
		const folderDepth = $from.depth - 1
		const folder = $from.node(folderDepth)
		const folderPos = $from.before(folderDepth)
		const file = state.schema.nodes.treeFile?.create()
		if (!file) return false
		const children: ProseMirrorNode[] = [file]
		folder.forEach((child, _offset, index) => {
			if (index > 0) children.push(child)
		})
		tr.replaceWith(folderPos, folderPos + folder.nodeSize, children)
		placeCaret(tr, folderPos + 1)
		return true
	}

	const tree = $from.node(1)
	if ($from.depth === 2 && tree.childCount === 1) {
		const treePos = $from.before(1)
		const paragraph = state.schema.nodes.paragraph?.create()
		if (!paragraph) return false
		tr.replaceWith(treePos, treePos + tree.nodeSize, paragraph)
		placeCaret(tr, treePos + 1)
		return true
	}
	const filePos = $from.before()
	tr.delete(filePos, filePos + $from.parent.nodeSize)
	tr.setSelection(Selection.near(tr.doc.resolve(Math.max(0, filePos - 1)), -1))
	return true
}

export const TreeFileExtension = Node.create({
	name: "treeFile",
	content: "text*",
	marks: "",
	defining: true,

	parseHTML() {
		return [{ tag: 'div[data-type="tree-file"]', priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "tree-file",
				class: "pl-tree-file",
			}),
			0,
		]
	},

	addInputRules() {
		// A trailing slash marks a folder, as in Mintlify's list syntax.
		return [
			new InputRule({
				find: /^([^/]+)\/$/,
				handler: ({ state, match }) => {
					const { $from } = state.selection
					if ($from.parent.type.name !== this.name) return null
					// Only when the slash ends the name, not partway through it.
					if ($from.parentOffset !== $from.parent.content.size) return null
					const { treeFolder, treeFolderName } = state.schema.nodes
					const name = match[1]?.trim()
					if (!treeFolder || !treeFolderName || !name) return null
					const filePos = $from.before()
					const folder = treeFolder.create({ open: true }, [
						treeFolderName.create(null, state.schema.text(name)),
					])
					state.tr.replaceWith(filePos, filePos + $from.parent.nodeSize, folder)
					placeCaret(state.tr, filePos + 2 + name.length)
				},
			}),
		]
	},
})

export const TreeFolderNameExtension = Node.create({
	name: "treeFolderName",
	content: "text*",
	marks: "",
	defining: true,

	parseHTML() {
		return [{ tag: 'summary[data-type="tree-name"]', priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"summary",
			mergeAttributes(HTMLAttributes, {
				"data-type": "tree-name",
				class: "pl-tree-name",
			}),
			0,
		]
	},
})

export const TreeFolderExtension = Node.create({
	name: "treeFolder",
	content: "treeFolderName (treeFolder | treeFile)*",
	defining: true,

	addAttributes() {
		return {
			open: {
				default: true,
				parseHTML: (element: HTMLElement) =>
					element.hasAttribute("open") ||
					element.getAttribute("data-open") === "true",
				renderHTML: (attributes: Record<string, unknown>) =>
					attributes.open ? { open: "" } : {},
			},
		}
	},

	parseHTML() {
		return [
			{ tag: 'details[data-type="tree-folder"]', priority: PARSE_PRIORITY },
		]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"details",
			mergeAttributes(HTMLAttributes, {
				"data-type": "tree-folder",
				class: "pl-tree-folder",
			}),
			0,
		]
	},
})

export const TreeExtension = Node.create({
	name: "tree",
	// Its keys run before the core Enter chain, which tries to split the line.
	priority: 200,
	group: "block",
	content: "(treeFolder | treeFile)+",
	defining: true,
	isolating: true,

	parseHTML() {
		return [{ tag: 'div[data-type="tree"]', priority: PARSE_PRIORITY }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "tree",
				class: "pl-tree",
			}),
			0,
		]
	},

	addCommands() {
		return {
			insertTree:
				() =>
				({ chain, state }) => {
					const from = state.selection.from
					return chain()
						.insertContent({
							type: this.name,
							content: [
								{
									type: "treeFolder",
									attrs: { open: true },
									content: [{ type: "treeFolderName" }, { type: "treeFile" }],
								},
							],
						})
						.command(({ tr, dispatch }) => {
							if (!dispatch) return true
							let target = -1
							tr.doc.descendants((node, pos) => {
								if (target >= 0) return false
								if (node.type.name === "treeFolderName" && pos >= from - 1) {
									target = pos
									return false
								}
								return true
							})
							if (target >= 0) placeCaret(tr, target + 1)
							return true
						})
						.run()
				},

			toggleTreeFolder:
				(pos) =>
				({ tr, state, dispatch }) => {
					const node = state.doc.nodeAt(pos)
					if (node?.type.name !== "treeFolder") return false
					if (dispatch) {
						tr.setNodeMarkup(pos, undefined, {
							...node.attrs,
							open: !node.attrs.open,
						})
					}
					return true
				},

			indentTreeItem:
				() =>
				({ state, tr, dispatch }) =>
					dispatch ? indent(state, tr) : currentItem(state) !== null,

			outdentTreeItem:
				() =>
				({ state, tr, dispatch }) =>
					dispatch ? outdent(state, tr) : currentItem(state) !== null,
		}
	},

	addKeyboardShortcuts() {
		const run =
			(apply: (state: EditorState, tr: Transaction) => boolean) => () =>
				this.editor.commands.command(({ state, tr }) => apply(state, tr))
		return {
			Tab: run(indent),
			"Shift-Tab": run(outdent),
			Enter: run(leaveEmptyFile),
			Backspace: run(removeEmptyRow),
		}
	},
})

export const TreeExtensions = [
	TreeExtension,
	TreeFolderExtension,
	TreeFolderNameExtension,
	TreeFileExtension,
]
